import { Drug } from "@/models/Drug/Drug";
import HasID from "./HasID";
import TrackedObject from "./TrackedObject";
import { DrugSource } from "./Drug/DrugProduct";
import { InventoryLocation } from "./InventoryLocation";
import { AssayBasis } from "./Inventory";

export interface InventoryBatch extends HasID, TrackedObject{
    storeID : number
    vendorID : number | null
    programID : number
    purchaseOrderNumber : string | null
    eDIFileID : number | null

    vendorName : string | null
    storeName : string | null
    programName : string | null
    linesQty : number
    isNDCProblem : boolean

    items : Array<InventoryBatch.InventoryBatchItem>

    submitted : Date | null
    submittedBy : string | null
    canceled : Date | null
    canceledBy : string | null

    isReturn: boolean;
    location: InventoryLocation;
    senderName: string
    invoiceNumber: string | null
    transactionDate: Date | null
}

export class InventoryBatch {
    constructor(obj? : InventoryBatch)
    {
        this.storeID = 0;
        // Not 0: VendorID is a nullable foreign key, so "no vendor" has to be null or the save fails on the
        // constraint rather than recording a batch with none.
        this.vendorID = null;
        this.vendorName = null;
        this.storeName = null;
        this.purchaseOrderNumber = null;
        this.isNDCProblem = false;
        this.items = new Array<InventoryBatch.InventoryBatchItem>();
        this.isReturn = false;

        if(obj){
            Object.assign(this, obj);
            this.items = obj.items?.map(i => Object.assign(new InventoryBatch.InventoryBatchItem(), i))
                || new Array<InventoryBatch.InventoryBatchItem>();
        }

        if (!this.location) {
            this.location = new InventoryLocation();
        }
    }
}

export namespace InventoryBatch{
    export interface InventoryBatchItem extends HasID, TrackedObject{
        drugSource : DrugSource
        packageID : number | null
        shippedQuantity : number
        receivedQuantity : number
        inventoryBatchId : number
        ediDrugDesc : string

        packageSize : number
        ndc : string | null
        productNameShort : string | null
        manufacturerName : string | null
        location: InventoryLocation
        lots: Array<InventoryBatchItemLot>
    }

    export class InventoryBatchItem {
        constructor(drug?:Drug)
        {
            this.packageID = 0;
            this.receivedQuantity = 0;
            this.shippedQuantity = 0;
            this.ndc = null;
            this.productNameShort = null;
            this.lots = [];

            if (drug)
            {
                this.applyDrug(drug);
            }

            if (!this.location) {
                this.location = new InventoryLocation();
            }
        }

        /**
         * Points this row at a drug. Every column the batch table reads about the drug is derived here, so a
         * row re-pointed at a different drug cannot keep half of the previous one's attributes.
         */
        applyDrug(drug: Drug): void {
            this.drugSource = drug.source;
            this.packageID = drug.drugId ? Number(drug.drugId) : null
            this.productNameShort = drug.productNameShort;
            this.packageSize = drug.packageSize;
            this.ndc = drug.ndc?.replace(/-/g,"");
        }

        get totalShippedUnits():number | null {
            return this.packageID ? this.shippedQuantity * this.packageSize : null;
        }

        get totalReceivedUnits():number | null {
            return this.packageID ? this.receivedQuantity * this.packageSize : null;
        }

        get drugName():string|null {
            return this.packageID ? this.productNameShort : this.ediDrugDesc;
        }
    }

    export interface InventoryBatchItemLot {
        lot: string;
        expiry: string;
        quantity: number;
        serialNumbers: string[];
        cost?: number;
        dateReceived?: string;
        // Certificate of Analysis entered at receiving; carried onto the inventory row on submit.
        assayPercent?: number | null;
        assayBasis?: AssayBasis | null;
        lodPercent?: number | null;
    }

    /** What the drug lookup answers with for a scanned code. It reads codes, not GS1 payloads. */
    export interface InventoryBatchBarcodeLookup {
        packageId: number | null;
        drugSource: DrugSource;
        packageSize: number | null;
    }

    /**
     * A scanned package: the drug its code resolved to, plus whatever lot and expiration the scan
     * itself carried. A linear UPC/NDC scan resolves the drug only.
     */
    export interface InventoryBatchBarcodeScan extends InventoryBatchBarcodeLookup {
        lotNumber: string | null;
        /** yyyy-MM-dd, or null when the scan carried no readable expiration. */
        expirationDate: string | null;
    }

    /** Lot numbers are matched trimmed and case-insensitively. */
    export function lotKey(lot?: string | null): string {
        return (lot ?? "").trim().toUpperCase();
    }

    /** Lot quantities come off the grid inputs as strings, so anything summing them has to coerce. */
    export function lotTotalQuantity(lots?: Array<InventoryBatchItemLot> | null): number {
        return (lots ?? []).reduce((total, l) => total + (Number(l.quantity) || 0), 0);
    }

    /**
     * Records one scanned package against `lots` and returns a new array. A lot already in the list has its
     * quantity moved by `quantityStep` rather than being added a second time, so scanning ten bottles of one
     * lot produces one row of ten.
     *
     * Expiration is only filled in where the row does not already have one: a date someone keyed, or an
     * earlier scan recorded, outranks a later read of the same lot.
     *
     * The same lot number always carries the same expiration on a manufacturer package, so expiry
     * deliberately takes no part in matching — including it would only split a lot into duplicate rows
     * when one read happened to lack the expiration.
     */
    export function mergeScannedLot(
        lots: Array<InventoryBatchItemLot> | null | undefined,
        scan: { lotNumber?: string | null; expirationDate?: string | null },
        quantityStep = 1
    ): Array<InventoryBatchItemLot> {
        const next = [...(lots ?? [])];

        // A row with no lot can never be valid - itemLotsAreValid rejects it - and the batch form does not
        // render the lot grid, so an operator handed one has no obvious way to delete it. Refuse instead.
        if (!lotKey(scan.lotNumber)) return next;

        const index = next.findIndex(l => lotKey(l.lot) === lotKey(scan.lotNumber));

        if (index < 0) {
            next.push({
                lot: (scan.lotNumber ?? "").trim(),
                expiry: scan.expirationDate ?? "",
                quantity: quantityStep,
                serialNumbers: []
            });
            return next;
        }

        // Replaced rather than mutated: rows loaded from the API can be missing `expiry` altogether, and
        // adding a key to an object Vue 2 has already observed goes unnoticed.
        next[index] = {
            ...next[index],
            expiry: next[index].expiry || scan.expirationDate || "",
            quantity: (Number(next[index].quantity) || 0) + quantityStep
        };

        return next;
    }
}
